import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Player, Controls } from '@lottiefiles/react-lottie-player';
import { useIntl } from 'react-intl';
import {FormattedMessage} from '@learningblocks/shared-components';
import {sanitizeConfigOptions} from '@labxchange/util-sanitization';
import './style.scss';

export function FullWidthAnimation(props) {

  const intl = useIntl(); // current language = intl.locale

  const player = useRef();
  const playerWrapper = useRef();
  const playerVisible = useOnScreen(playerWrapper);

  const [loaded, setLoaded] = useState(false);
  const [playerRun, setPlayerRun] = useState(false);

  const [src, setSrc] = useState(
    props.preview
      ? props.url
      : props.src.includes('animations/')
      ? props.src
      : window.location.origin +
        window.location.pathname +
        `locales/${intl.locale}/animations/${props.src}`
  );

  useEffect(()=>{
    if (loaded) {
      if (props.autoplay){
        player.current.play();
      }
    }
  }, [loaded])

  useEffect(() => {
    if (playerVisible && player.current && !playerRun) {
      player.current.play();
      setPlayerRun(true)
    }
  }, [playerVisible, playerRun]);

  useEffect(() => {
    props.preview
      ? props.url
      : props.src.includes('animation/')
      ? setSrc(props.src)
      : setSrc(
          window.location.origin +
            window.location.pathname +
            `locales/${intl.locale}/animations/${props.src}`
        );
  }, [intl.locale]);

  // When the image load errors, set the src to use the English path
  const handleLoadError = () => {
    console.log('Animation load error, attempting fallback...');

    props.preview
      ? props.url
      : props.src.includes('animations/')
      ? setSrc(props.src)
      : setSrc(
          window.location.origin +
            window.location.pathname +
            `locales/en/animations/${props.src}`
        );
  };

  const handleLoaded = () => {
    const wrapper = document.getElementById(`testingClassStyle_${src}`);
    const height = wrapper.offsetHeight;
    const newHeight = player.current.container.offsetHeight;

    wrapper.style.cssText = `height: ${height}px; aspect-ratio: auto;`;
    wrapper.offsetHeight;
    wrapper.style.height = `${newHeight + 40}px`;

    const overlay = wrapper.querySelector('.fullWidthAnimation-overlay');
    overlay.style.opacity = 0;

    setTimeout(function() {
      wrapper.style.height = `fit-content`;
      setLoaded(true);
    }, 300);
  };

  const animation = (
    <Player
      ref={player}
      key={src} // used to re-render when src changes
      loop={(props.loop !== '')?props.loop:true}
      // autoplay
      controls
      src={src}
      onEvent={event => {
        if (event === 'load' ) handleLoaded();
        if (event === 'error') handleLoadError();
      }}
    >
      <Controls visible={true} buttons={['play', 'stop']} />
    </Player>
  );

  const loadingState = (
    <div
      className='fullWidthAnimation-overlay'
    >
      <FormattedMessage
          string={'Loading'}
          description=""
      />
    </div>
  );

  return (
    <figure 
      className='fullWidthAnimation'
      aria-label={FormattedMessage({
        "string": props.altText,
      })}
    >
      <div id={`testingClassStyle_${src}`} className='testingClassStyle'>
        {animation}
        {!loaded ? loadingState : null}
      </div>
      {props.caption !== '' ?
        <figcaption>
          <FormattedMessage
            string={props.caption}
            sanitizeConfig={sanitizeConfigOptions.BlockText}
            description=""
          />
        </figcaption>
      : null}
    </figure>
  );
}

FullWidthAnimation.propTypes = {
  src: PropTypes.string.isRequired,
  url: PropTypes.string,
  altText: PropTypes.string.isRequired,
  caption: PropTypes.string,
  loop: PropTypes.bool,
  autoplay: PropTypes.bool,
  preview: PropTypes.bool,
};

FullWidthAnimation.defaultProps = {
  loop: false,
  autoplay: false,
  preview: false,
};


export function useOnScreen(ref) {
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = new IntersectionObserver(
    ([entry]) => setIntersecting(entry.isIntersecting),
    { rootMargin: '-50% 0%' }
  );

  useEffect(() => {
    if (ref.current) {
      observer.observe(ref.current);
      // Remove the observer as soon as the component is unmounted
      return () => {
        observer.disconnect();
      };
    }
  }, [ref.current]);

  return isIntersecting;
}
